import React, { useState, useEffect } from "react";
import { useFetch, api } from "@/lib/api";
import { toast } from "sonner";
import { Rocket } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, StatusChip, ConfidenceBadge, LoadingState } from "@/components/app/primitives";

export default function LaunchConsolePage() {
  const { data: overview, loading, refetch } = useFetch("/overview");
  const { refreshOverview } = useApp();
  const [objective, setObjective] = useState("");
  const [riskTolerance, setRiskTolerance] = useState("");
  const [pilotAmbition, setPilotAmbition] = useState("");
  const [launching, setLaunching] = useState(false);

  const state = overview?.state;
  useEffect(() => {
    if (state) {
      setObjective(state.config.objective);
      setRiskTolerance(state.config.risk_tolerance);
      setPilotAmbition(state.config.pilot_ambition);
    }
  }, [state]);

  if (loading) return <LoadingState />;

  const launch = async () => {
    setLaunching(true);
    try {
      await api.post("/assessment/launch", { objective, risk_tolerance: riskTolerance, pilot_ambition: pilotAmbition });
      toast.success("Assessment configuration confirmed", { description: "Week 1 plan and question packs are live in the Question Builder." });
      refetch();
      refreshOverview();
    } catch (e) {
      toast.error("Launch failed");
    } finally {
      setLaunching(false);
    }
  };

  const cfg = state?.config;

  return (
    <div data-testid="launch-page">
      <PageHeader
        kicker="Scene 2 - Assessment launch"
        title="Assessment Launch Console"
        subtitle="Configure the engagement frame: objective, metric, horizons, stakeholders, hypotheses, and pilot ambition. Launching generates the Week 1 plan and required questions."
        right={
          <button onClick={launch} disabled={launching} data-testid="assessment-launch-button"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-4 py-2 text-xs font-semibold text-white transition-colors disabled:opacity-50">
            <Rocket size={13} /> {launching ? "Confirming..." : state?.status === "launched" ? "Re-confirm launch" : "Launch assessment"}
          </button>
        }
      />

      {state?.status === "launched" && (
        <div className="rounded-lg border border-[--green] bg-[--green-soft] px-4 py-2.5 mb-5 flex items-center gap-2" data-testid="launch-status-banner">
          <span className="w-2 h-2 rounded-full bg-[--green]" />
          <span className="text-xs font-medium text-[--tx-1]">Assessment launched - {state.phase}. Agents ran against current evidence; cadence operating since Day 1.</span>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Engagement configuration" testId="launch-config-panel">
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-[--tx-3] block mb-1.5">Assessment objective</label>
              <textarea value={objective} onChange={(e) => setObjective(e.target.value)} rows={2} data-testid="launch-objective-input"
                className="w-full rounded-lg bg-[--shell-2] border border-[--hairline] px-3 py-2 text-[13px] text-[--tx-1] focus:outline-none focus:ring-2 focus:ring-[--ring] resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-[--tx-3] block mb-1.5">Primary metric</label>
                <div className="rounded-lg bg-[--shell-2] border border-[--hairline] px-3 py-2 text-[13px] text-[--tx-1]">{cfg?.primary_metric}</div>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-[--tx-3] block mb-1.5">Time horizons</label>
                <div className="flex flex-wrap gap-1.5 pt-1.5">
                  {(cfg?.time_horizons || []).map((h) => <StatusChip key={h} tone="crimson">{h}</StatusChip>)}
                </div>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-[--tx-3] block mb-1.5">Risk tolerance</label>
              <textarea value={riskTolerance} onChange={(e) => setRiskTolerance(e.target.value)} rows={2} data-testid="launch-risk-tolerance-input"
                className="w-full rounded-lg bg-[--shell-2] border border-[--hairline] px-3 py-2 text-[13px] text-[--tx-1] focus:outline-none focus:ring-2 focus:ring-[--ring] resize-none" />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-[--tx-3] block mb-1.5">Pilot ambition</label>
              <textarea value={pilotAmbition} onChange={(e) => setPilotAmbition(e.target.value)} rows={2} data-testid="launch-pilot-ambition-input"
                className="w-full rounded-lg bg-[--shell-2] border border-[--hairline] px-3 py-2 text-[13px] text-[--tx-1] focus:outline-none focus:ring-2 focus:ring-[--ring] resize-none" />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-[--tx-3] block mb-1.5">Stakeholder groups</label>
              <div className="flex flex-wrap gap-1.5">
                {(cfg?.stakeholder_groups || []).map((s) => <StatusChip key={s} tone="neutral">{s}</StatusChip>)}
              </div>
            </div>
          </div>
        </Panel>

        <div className="space-y-4">
          <Panel title="Initial hypotheses register" testId="launch-hypotheses-panel">
            <div className="space-y-2">
              {(cfg?.hypotheses || []).map((h) => (
                <div key={h.id} className="flex items-start gap-2.5 rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2" data-testid={`hypothesis-row-${h.id}`}>
                  <span className="font-mono text-[10px] text-[--tx-3] pt-0.5 shrink-0">{h.id}</span>
                  <p className="text-xs text-[--tx-2] leading-relaxed flex-1">{h.text}</p>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <ConfidenceBadge level={h.status} />
                    <span className="font-mono text-[10px] text-[--tx-3]">{h.rcc}</span>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Week 1 assessment plan (generated)" testId="launch-week1-panel">
            <div className="space-y-2 text-[13px] text-[--tx-2]">
              {[
                { d: "Day 1", t: "Metric reconciliation scope; prior-work intake; owner map; document/data intake open; daily rhythm live" },
                { d: "Day 3", t: "First extracts loaded; order/cancel/install/save/fallout fields reconciled" },
                { d: "Day 5", t: "Gap scan, evidence map, root-cause hypothesis map, data-confidence view" },
                { d: "Day 6", t: "Metric reconciliation session (Sales Ops + Finance + IT)" },
                { d: "Day 7", t: "Pilot cohort selection; Triad gate; portfolio stop/scale/protect/integrate; journey owner decision" },
              ].map((r) => (
                <div key={r.d} className="flex gap-3">
                  <span className="font-mono text-[11px] text-[#E86A6A] w-12 shrink-0 pt-0.5">{r.d}</span>
                  <span className="leading-relaxed">{r.t}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-[--divider] text-xs text-[--tx-3]">
              Required Week 1 questions are staged in the Question Builder, organized by audience with evidence-gap mapping.
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
